/**
 * Route remote image URLs through the backend image-CDN proxy so we get
 * tiny webp thumbnails instead of the original (often multi-MB) source.
 *
 * Base64 data URLs are returned unchanged — feeding a 2 MB data URL into
 * a query string would actually *grow* the request. Those should already
 * be small thanks to the server-side upload-shrinker (v44).
 *
 * v79.14 — Object-storage assets (/api/files/...) used to be served at
 * the full uploaded resolution (often 2400×1800 / 2-3 MB) to every
 * client, including the Capacitor Android WebView. That kept older
 * phones stuck on a blank grid for 3-5 s while the network drained the
 * payload. The backend now accepts `?w=N` on /api/files/{path} to
 * serve a resized + WebP/AVIF-transcoded copy, so we forward the width
 * hint here.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function cdnImage(src, width = 400) {
    if (!src || typeof src !== "string") return src;
    if (src.startsWith("data:")) return src;
    // v78.13 — Object-storage refs come back as relative `/api/files/...`
    // paths. v79.14 — forward the requested width so the backend serves
    // a downscaled / re-encoded copy instead of the multi-MB original.
    if (src.startsWith("/api/files/")) {
        const sep = src.includes("?") ? "&" : "?";
        return `${API_BASE}${src}${sep}w=${width}`;
    }
    // The /api/listings/{id}/thumb endpoint already takes ?w=; preserve
    // an existing width if the backend supplied one, otherwise inject ours.
    if (src.startsWith("/api/listings/") && src.includes("/thumb")) {
        if (/[?&]w=\d+/.test(src)) return `${API_BASE}${src}`;
        const sep = src.includes("?") ? "&" : "?";
        return `${API_BASE}${src}${sep}w=${width}`;
    }
    if (!src.startsWith("http")) return src;
    return `${API_BASE}/api/img?url=${encodeURIComponent(src)}&w=${width}`;
}
